import { useState, useEffect, useMemo } from "react";
import { creditNotesAPI, suppliersAPI, projectsAPI } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { Plus, Edit, Trash2, FileText, Search, CheckCircle, XCircle, Ban, Euro, Calendar, Building2, Loader2, Download, FileDown, Users } from "lucide-react";
import { Link } from "react-router-dom";

const STATUS_BADGES = {
  draft: { label: "Entwurf", variant: "secondary" },
  final: { label: "Finalisiert", variant: "default" },
  paid: { label: "Bezahlt", variant: "success" },
  cancelled: { label: "Storniert", variant: "destructive" }
};

const COMMISSION_TYPES = {
  grundprovision: "Grundprovision",
  ueberverkauf: "Überverkauf"
};

export default function CreditNotesPage() {
  const [creditNotes, setCreditNotes] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingCreditNote, setEditingCreditNote] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [actionLoading, setActionLoading] = useState(null);
  
  const [formData, setFormData] = useState({
    supplier_id: "",
    line_items: [],
    notes: "",
    internal_notes: ""
  });

  const [newLineItem, setNewLineItem] = useState({
    project_id: "",
    commission_type: "grundprovision",
    commission_basis: 0,
    commission_rate: 7,
    commission_amount: 0,
    signature_date: "",
    description: ""
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [creditNotesRes, suppliersRes, projectsRes] = await Promise.all([
        creditNotesAPI.list(),
        suppliersAPI.list(),
        projectsAPI.list()
      ]);
      setCreditNotes(creditNotesRes.data);
      setSuppliers(suppliersRes.data);
      setProjects(projectsRes.data);
    } catch (error) {
      console.error("Failed to load data:", error);
      toast.error("Fehler beim Laden der Daten");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      supplier_id: "",
      line_items: [],
      include_vat: true,
      vat_rate: 19,
      notes: "",
      internal_notes: ""
    });
    setNewLineItem({
      project_id: "",
      commission_type: "grundprovision",
      commission_basis: 0,
      commission_rate: 7,
      commission_amount: 0,
      signature_date: "",
      description: ""
    });
    setEditingCreditNote(null);
  };

  const calculateCommission = (basis, rate) => {
    return Math.round(basis * (rate / 100) * 100) / 100;
  };

  const handleLineItemChange = (field, value) => {
    const updated = { ...newLineItem, [field]: value };
    
    // Auto-calculate commission amount
    if (field === "commission_basis" || field === "commission_rate") {
      const basis = field === "commission_basis" ? parseFloat(value) || 0 : parseFloat(updated.commission_basis) || 0;
      const rate = field === "commission_rate" ? parseFloat(value) || 0 : parseFloat(updated.commission_rate) || 0;
      updated.commission_amount = calculateCommission(basis, rate);
    }
    
    // Auto-generate description
    if (field === "commission_type" || field === "commission_basis" || field === "commission_rate") {
      const typeLabel = COMMISSION_TYPES[updated.commission_type] || updated.commission_type;
      updated.description = `${typeLabel} ${updated.commission_rate}% von ${updated.commission_basis.toLocaleString("de-DE")}€`;
    }
    
    setNewLineItem(updated);
  };

  const addLineItem = () => {
    if (!newLineItem.project_id || newLineItem.commission_amount <= 0) {
      toast.error("Bitte Projekt auswählen und Provisionsbetrag eingeben");
      return;
    }
    
    const project = projects.find(p => p.id === newLineItem.project_id);
    
    setFormData({
      ...formData,
      line_items: [
        ...formData.line_items,
        {
          ...newLineItem,
          project_number: project?.project_number,
          customer_name: project?.customer_name
        }
      ]
    });
    
    setNewLineItem({
      project_id: "",
      commission_type: "grundprovision",
      commission_basis: 0,
      commission_rate: 7,
      commission_amount: 0,
      signature_date: "",
      description: ""
    });
  };

  const removeLineItem = (index) => {
    setFormData({
      ...formData,
      line_items: formData.line_items.filter((_, i) => i !== index)
    });
  };

  const getTotalNet = () => {
    return formData.line_items.reduce((sum, item) => sum + (item.commission_amount || 0), 0);
  };

  const getTotalVat = () => {
    if (!formData.include_vat) return 0;
    return getTotalNet() * (formData.vat_rate / 100);
  };

  const getTotalGross = () => {
    return getTotalNet() + getTotalVat();
  };

  // Auto-set include_vat based on supplier type when supplier changes
  useEffect(() => {
    if (formData.supplier_id) {
      const selectedSupplier = suppliers.find(s => s.id === formData.supplier_id);
      if (selectedSupplier) {
        // Tippgeber (Privatpersonen) -> ohne MwSt., Vertriebler -> mit MwSt.
        setFormData(prev => ({
          ...prev,
          include_vat: selectedSupplier.supplier_type !== "tippgeber"
        }));
      }
    }
  }, [formData.supplier_id, suppliers]);

  // Filter projects based on selected supplier's linked user
  const filteredProjects = useMemo(() => {
    if (!formData.supplier_id) return projects;
    
    const selectedSupplier = suppliers.find(s => s.id === formData.supplier_id);
    if (!selectedSupplier) return projects;
    
    // If supplier is a "vertriebler" and has a linked user, filter projects
    if (selectedSupplier.supplier_type === "vertriebler" && selectedSupplier.linked_user_id) {
      return projects.filter(p => p.created_by_id === selectedSupplier.linked_user_id);
    }
    
    // For "tippgeber" or unlinked suppliers, show all projects
    return projects;
  }, [formData.supplier_id, suppliers, projects]);

  const handleDownloadPdf = async (creditNote) => {
    try {
      const response = await creditNotesAPI.downloadPdf(creditNote.id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Gutschrift_${creditNote.credit_note_number || 'Entwurf'}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success("PDF heruntergeladen");
    } catch (error) {
      console.error("Failed to download PDF:", error);
      toast.error("Fehler beim Herunterladen des PDFs");
    }
  };

  const handleExportAll = async () => {
    try {
      const response = await creditNotesAPI.exportAll();
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Gutschriften_Export_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success("Export heruntergeladen");
    } catch (error) {
      console.error("Failed to export:", error);
      toast.error("Fehler beim Export");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.supplier_id) {
      toast.error("Bitte Lieferanten auswählen");
      return;
    }
    
    if (formData.line_items.length === 0) {
      toast.error("Bitte mindestens eine Position hinzufügen");
      return;
    }
    
    try {
      if (editingCreditNote) {
        await creditNotesAPI.update(editingCreditNote.id, formData);
        toast.success("Gutschrift aktualisiert");
      } else {
        await creditNotesAPI.create(formData);
        toast.success("Gutschrift erstellt");
      }
      setShowDialog(false);
      resetForm();
      loadData();
    } catch (error) {
      console.error("Failed to save credit note:", error);
      toast.error(error.response?.data?.detail || "Fehler beim Speichern");
    }
  };

  const handleEdit = (creditNote) => {
    if (creditNote.status !== "draft") {
      toast.error("Nur Entwürfe können bearbeitet werden");
      return;
    }
    setEditingCreditNote(creditNote);
    setFormData({
      supplier_id: creditNote.supplier_id,
      line_items: creditNote.line_items || [],
      include_vat: creditNote.include_vat !== false,  // Default to true if not set
      vat_rate: creditNote.vat_rate ?? 19,
      notes: creditNote.notes || "",
      internal_notes: creditNote.internal_notes || ""
    });
    setShowDialog(true);
  };

  const handleFinalize = async (creditNote) => {
    if (!window.confirm("Möchten Sie diese Gutschrift finalisieren? Sie kann danach nicht mehr bearbeitet werden.")) return;
    
    setActionLoading(creditNote.id);
    try {
      await creditNotesAPI.finalize(creditNote.id);
      toast.success("Gutschrift finalisiert");
      loadData();
    } catch (error) {
      console.error("Failed to finalize:", error);
      toast.error(error.response?.data?.detail || "Fehler beim Finalisieren");
    } finally {
      setActionLoading(null);
    }
  };

  const handleCancel = async (creditNote) => {
    if (!window.confirm("Möchten Sie diese Gutschrift stornieren? Eine Storno-Gutschrift wird erstellt.")) return;
    
    setActionLoading(creditNote.id);
    try {
      await creditNotesAPI.cancel(creditNote.id);
      toast.success("Gutschrift storniert");
      loadData();
    } catch (error) {
      console.error("Failed to cancel:", error);
      toast.error(error.response?.data?.detail || "Fehler beim Stornieren");
    } finally {
      setActionLoading(null);
    }
  };

  const handleMarkPaid = async (creditNote) => {
    setActionLoading(creditNote.id);
    try {
      await creditNotesAPI.markPaid(creditNote.id);
      toast.success("Gutschrift als bezahlt markiert");
      loadData();
    } catch (error) {
      console.error("Failed to mark as paid:", error);
      toast.error(error.response?.data?.detail || "Fehler beim Markieren");
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (creditNote) => {
    if (creditNote.status !== "draft") {
      toast.error("Nur Entwürfe können gelöscht werden");
      return;
    }
    
    if (!window.confirm("Möchten Sie diesen Entwurf wirklich löschen?")) return;
    
    try {
      await creditNotesAPI.delete(creditNote.id);
      toast.success("Entwurf gelöscht");
      loadData();
    } catch (error) {
      console.error("Failed to delete:", error);
      toast.error(error.response?.data?.detail || "Fehler beim Löschen");
    }
  };

  const filteredCreditNotes = creditNotes.filter(cn => {
    const matchesSearch = 
      cn.credit_note_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      cn.supplier_name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filterStatus === "all" || cn.status === filterStatus;
    
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="credit-notes-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Provisionsgutschriften</h1>
          <p className="text-muted-foreground">Provisionen für Vertriebler und Tippgeber</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" asChild data-testid="tab-customer-credit-notes">
            <Link to="/gutschriften-kunden">
              <Users className="h-4 w-4 mr-2" />
              Kunden-Gutschriften
            </Link>
          </Button>
          <Button variant="outline" onClick={handleExportAll} data-testid="export-credit-notes-btn">
            <FileDown className="h-4 w-4 mr-2" />
            Export (CSV)
          </Button>
          <Button onClick={() => { resetForm(); setShowDialog(true); }} data-testid="add-credit-note-btn">
            <Plus className="h-4 w-4 mr-2" />
            Neue Gutschrift
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Suchen..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="credit-note-search"
          />
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-48" data-testid="credit-note-status-filter">
            <SelectValue placeholder="Alle Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle Status</SelectItem>
            <SelectItem value="draft">Entwürfe</SelectItem>
            <SelectItem value="final">Finalisiert</SelectItem>
            <SelectItem value="paid">Bezahlt</SelectItem>
            <SelectItem value="cancelled">Storniert</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold">{creditNotes.length}</div>
            <p className="text-xs text-muted-foreground">Gesamt</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-orange-600">
              {creditNotes.filter(cn => cn.status === "draft").length}
            </div>
            <p className="text-xs text-muted-foreground">Entwürfe</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-blue-600">
              {creditNotes.filter(cn => cn.status === "final").length}
            </div>
            <p className="text-xs text-muted-foreground">Offen</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-green-600">
              {creditNotes.filter(cn => cn.status === "paid").reduce((sum, cn) => sum + cn.gross_amount, 0).toLocaleString("de-DE")}€
            </div>
            <p className="text-xs text-muted-foreground">Bezahlt</p>
          </CardContent>
        </Card>
      </div>

      {/* Credit Notes List */}
      <div className="space-y-4">
        {filteredCreditNotes.length === 0 ? (
          <Card>
            <CardContent className="text-center py-12">
              <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">Keine Gutschriften gefunden</p>
            </CardContent>
          </Card>
        ) : (
          filteredCreditNotes.map((creditNote) => {
            const statusBadge = STATUS_BADGES[creditNote.status] || STATUS_BADGES.draft;
            const isLoading = actionLoading === creditNote.id;
            
            return (
              <Card 
                key={creditNote.id} 
                className={`hover:shadow-md transition-shadow ${creditNote.status === "cancelled" ? "opacity-60" : ""}`}
                data-testid={`credit-note-card-${creditNote.id}`}
              >
                <CardHeader className="pb-2">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="flex items-center gap-2">
                        <CardTitle className="text-lg">
                          {creditNote.credit_note_number || "Entwurf"}
                        </CardTitle>
                        <Badge variant={statusBadge.variant}>{statusBadge.label}</Badge>
                      </div>
                      <CardDescription>
                        {creditNote.supplier_name} • {new Date(creditNote.created_at).toLocaleDateString("de-DE")}
                      </CardDescription>
                    </div>
                    <div className="flex gap-2">
                      {/* PDF Download Button - always visible for finalized/paid */}
                      {creditNote.status !== "draft" && creditNote.status !== "cancelled" && (
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => handleDownloadPdf(creditNote)}
                          data-testid={`download-pdf-${creditNote.id}`}
                        >
                          <Download className="h-4 w-4 mr-1" />
                          PDF
                        </Button>
                      )}
                      {creditNote.status === "draft" && (
                        <>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => handleFinalize(creditNote)}
                            disabled={isLoading}
                            data-testid={`finalize-credit-note-${creditNote.id}`}
                          >
                            {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-1" />}
                            Finalisieren
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleEdit(creditNote)} data-testid={`edit-credit-note-${creditNote.id}`}>
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDelete(creditNote)} className="text-destructive" data-testid={`delete-credit-note-${creditNote.id}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                      {creditNote.status === "final" && (
                        <>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => handleMarkPaid(creditNote)}
                            disabled={isLoading}
                            data-testid={`mark-paid-credit-note-${creditNote.id}`}
                          >
                            {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Euro className="h-4 w-4 mr-1" />}
                            Als bezahlt
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => handleCancel(creditNote)}
                            disabled={isLoading}
                            className="text-destructive"
                            data-testid={`cancel-credit-note-${creditNote.id}`}
                          >
                            {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Ban className="h-4 w-4 mr-1" />}
                            Stornieren
                          </Button>
                        </>
                      )}
                      {creditNote.status === "paid" && (
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => handleCancel(creditNote)}
                          disabled={isLoading}
                          className="text-destructive"
                          data-testid={`cancel-credit-note-${creditNote.id}`}
                        >
                          {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Ban className="h-4 w-4 mr-1" />}
                          Stornieren
                        </Button>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {/* Line Items Preview */}
                    <div className="text-sm">
                      {creditNote.line_items?.slice(0, 3).map((item, idx) => (
                        <div key={idx} className="flex justify-between text-muted-foreground">
                          <span>{item.project_number} - {item.customer_name}: {item.description}</span>
                          <span>{item.commission_amount?.toLocaleString("de-DE")}€</span>
                        </div>
                      ))}
                      {creditNote.line_items?.length > 3 && (
                        <div className="text-muted-foreground">... und {creditNote.line_items.length - 3} weitere</div>
                      )}
                    </div>
                    
                    {/* Totals */}
                    <div className="flex justify-end gap-6 pt-2 border-t text-sm">
                      <div>
                        <span className="text-muted-foreground">Netto:</span>{" "}
                        <span className="font-medium">{creditNote.net_amount?.toLocaleString("de-DE")}€</span>
                      </div>
                      {creditNote.include_vat !== false ? (
                        <div>
                          <span className="text-muted-foreground">MwSt ({creditNote.vat_rate}%):</span>{" "}
                          <span className="font-medium">{creditNote.vat_amount?.toLocaleString("de-DE")}€</span>
                        </div>
                      ) : (
                        <div>
                          <span className="text-orange-600 text-xs">Ohne MwSt.</span>
                        </div>
                      )}
                      <div>
                        <span className="text-muted-foreground">Brutto:</span>{" "}
                        <span className="font-bold text-lg">{creditNote.gross_amount?.toLocaleString("de-DE")}€</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingCreditNote ? "Gutschrift bearbeiten" : "Neue Gutschrift"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Supplier Selection */}
            <div className="space-y-2">
              <Label>Lieferant *</Label>
              <Select value={formData.supplier_id} onValueChange={(v) => setFormData({ ...formData, supplier_id: v })}>
                <SelectTrigger data-testid="credit-note-supplier-select">
                  <SelectValue placeholder="Lieferant auswählen" />
                </SelectTrigger>
                <SelectContent>
                  {suppliers.map((supplier) => (
                    <SelectItem key={supplier.id} value={supplier.id}>
                      {supplier.supplier_number} - {supplier.company_name || `${supplier.first_name} ${supplier.last_name}`}
                      {supplier.supplier_type === "tippgeber" && " (Tippgeber)"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* VAT Switch */}
            <Card className={!formData.include_vat ? "border-orange-300 bg-orange-50" : ""}>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="include-vat" className="text-base font-medium">Mehrwertsteuer</Label>
                    <p className="text-sm text-muted-foreground">
                      {formData.include_vat 
                        ? "Mit MwSt. (19%) - für Unternehmen/Vertriebler" 
                        : "Ohne MwSt. - für Privatpersonen/Tippgeber"
                      }
                    </p>
                  </div>
                  <Switch
                    id="include-vat"
                    checked={formData.include_vat}
                    onCheckedChange={(checked) => setFormData({ ...formData, include_vat: checked })}
                    data-testid="include-vat-switch"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Add Line Item */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Position hinzufügen</CardTitle>
                {formData.supplier_id && suppliers.find(s => s.id === formData.supplier_id)?.supplier_type === "vertriebler" && 
                 suppliers.find(s => s.id === formData.supplier_id)?.linked_user_id && (
                  <p className="text-xs text-muted-foreground">
                    Nur Projekte des verknüpften Vertrieblers werden angezeigt
                  </p>
                )}
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Projekt *</Label>
                    <Select value={newLineItem.project_id} onValueChange={(v) => handleLineItemChange("project_id", v)}>
                      <SelectTrigger data-testid="line-item-project-select">
                        <SelectValue placeholder="Projekt auswählen" />
                      </SelectTrigger>
                      <SelectContent>
                        {filteredProjects.length === 0 ? (
                          <SelectItem value="none" disabled>Keine Projekte verfügbar</SelectItem>
                        ) : (
                          filteredProjects.map((project) => (
                            <SelectItem key={project.id} value={project.id}>
                              {project.project_number} - {project.customer_name}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Provisionstyp</Label>
                    <Select value={newLineItem.commission_type} onValueChange={(v) => handleLineItemChange("commission_type", v)}>
                      <SelectTrigger data-testid="line-item-commission-type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="grundprovision">Grundprovision</SelectItem>
                        <SelectItem value="ueberverkauf">Überverkauf</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="grid grid-cols-4 gap-4">
                  <div className="space-y-2">
                    <Label>Basis (€)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={newLineItem.commission_basis}
                      onChange={(e) => handleLineItemChange("commission_basis", parseFloat(e.target.value) || 0)}
                      data-testid="line-item-basis"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Satz (%)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={newLineItem.commission_rate}
                      onChange={(e) => handleLineItemChange("commission_rate", parseFloat(e.target.value) || 0)}
                      data-testid="line-item-rate"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Betrag (€)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={newLineItem.commission_amount}
                      onChange={(e) => setNewLineItem({ ...newLineItem, commission_amount: parseFloat(e.target.value) || 0 })}
                      data-testid="line-item-amount"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Unterschriftsdatum</Label>
                    <Input
                      type="date"
                      value={newLineItem.signature_date}
                      onChange={(e) => handleLineItemChange("signature_date", e.target.value)}
                      data-testid="line-item-signature-date"
                    />
                  </div>
                </div>
                
                <Button type="button" variant="outline" onClick={addLineItem} data-testid="add-line-item-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Position hinzufügen
                </Button>
              </CardContent>
            </Card>

            {/* Line Items List */}
            {formData.line_items.length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Positionen ({formData.line_items.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {formData.line_items.map((item, index) => (
                      <div key={index} className="flex justify-between items-center p-2 bg-muted rounded">
                        <div className="flex-1">
                          <div className="font-medium">{item.project_number || item.project_id} - {item.customer_name}</div>
                          <div className="text-sm text-muted-foreground">{item.description}</div>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className="font-medium">{item.commission_amount?.toLocaleString("de-DE")}€</span>
                          <Button type="button" variant="ghost" size="icon" onClick={() => removeLineItem(index)}>
                            <XCircle className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </div>
                    ))}
                    
                    <div className="flex justify-end pt-4 border-t">
                      <div className="text-right">
                        <div className="text-sm text-muted-foreground">Netto: {getTotalNet().toLocaleString("de-DE")}€</div>
                        {formData.include_vat ? (
                          <>
                            <div className="text-sm text-muted-foreground">MwSt ({formData.vat_rate}%): {getTotalVat().toLocaleString("de-DE")}€</div>
                            <div className="text-lg font-bold">Brutto: {getTotalGross().toLocaleString("de-DE")}€</div>
                          </>
                        ) : (
                          <>
                            <div className="text-sm text-orange-600">Ohne MwSt.</div>
                            <div className="text-lg font-bold">Auszahlung: {getTotalNet().toLocaleString("de-DE")}€</div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Notes */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Notizen (auf Gutschrift)</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Öffentliche Notizen..."
                  data-testid="credit-note-notes"
                />
              </div>
              <div className="space-y-2">
                <Label>Interne Notizen</Label>
                <Textarea
                  value={formData.internal_notes}
                  onChange={(e) => setFormData({ ...formData, internal_notes: e.target.value })}
                  placeholder="Nur intern sichtbar..."
                  data-testid="credit-note-internal-notes"
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                Abbrechen
              </Button>
              <Button type="submit" data-testid="save-credit-note-btn">
                {editingCreditNote ? "Speichern" : "Erstellen"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
